import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, MapPin } from "lucide-react";

const HERO_IMG =
  "https://images.pexels.com/photos/17720190/pexels-photo-17720190.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const ICE_TEXTURE =
  "https://images.pexels.com/photos/38891176/pexels-photo-38891176.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const LINES = [
  { text: "THE COLD", cls: "text-snow" },
  { text: "NEVER", cls: "text-outline" },
  { text: "BREAKS.", cls: "text-frost" },
];

const STATS = [
  { value: "-25°C", label: "Coldest storage temp" },
  { value: "100%", label: "On-time metro" },
  { value: "24/7", label: "Live dispatch" },
  { value: "650+", label: "Suburbs served" },
];

const MaskedLine = ({ text, cls, index }) => (
  <div className="overflow-hidden py-1">
    <motion.span
      className={`block font-display font-semibold uppercase leading-[0.9] tracking-tighter text-5xl sm:text-6xl md:text-7xl lg:text-[6.5rem] ${cls}`}
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay: 0.25 + index * 0.14, ease: [0.16, 1, 0.3, 1] }}
    >
      {text}
    </motion.span>
  </div>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const yGlow = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col justify-end overflow-hidden pt-32">
      <motion.img
        src={ICE_TEXTURE}
        alt=""
        aria-hidden="true"
        style={{ y: yGlow }}
        className="absolute inset-0 w-full h-full object-cover opacity-[0.12] pointer-events-none"
      />
      <div className="absolute top-1/4 right-[10%] w-[420px] h-[420px] bg-frost/10 blur-[140px] rounded-full pointer-events-none" aria-hidden="true" />

      <motion.div style={{ opacity }} className="relative max-w-[1440px] mx-auto w-full px-6 lg:px-12 pb-16">
        <div className="grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <motion.p
              data-testid="hero-overline"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="font-mono text-xs uppercase tracking-[0.25em] text-frost font-bold mb-8 flex items-center gap-3"
            >
              <span className="w-8 h-px bg-frost inline-block" />
              Refrigerated courier — Sydney, Australia
            </motion.p>

            <h1 data-testid="hero-headline">
              {LINES.map((line, i) => (
                <MaskedLine key={line.text} text={line.text} cls={line.cls} index={i} />
              ))}
            </h1>

            <motion.p
              data-testid="hero-subcopy"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.85, ease: [0.16, 1, 0.3, 1] }}
              className="mt-8 max-w-xl text-base lg:text-lg leading-relaxed text-mist font-light"
            >
              Same-day refrigerated transport across Greater Sydney. Chilled, frozen and
              pharmaceutical freight — collected, carried and delivered degree by degree,
              with an unbroken chain of custody from dock to door.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1, ease: [0.16, 1, 0.3, 1] }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <a
                href="#quote"
                data-testid="hero-quote-btn"
                className="inline-flex items-center gap-3 bg-blaze text-white font-mono text-xs uppercase tracking-[0.15em] px-8 py-4 hover:bg-frost hover:text-abyss transition-colors duration-300 active:scale-95"
              >
                Get an instant quote
                <ArrowDownRight className="w-4 h-4" strokeWidth={1.5} />
              </a>
              <a
                href="#services"
                data-testid="hero-services-btn"
                className="inline-flex items-center gap-3 border border-white/20 text-snow font-mono text-xs uppercase tracking-[0.15em] px-8 py-4 hover:border-frost hover:text-frost transition-colors duration-300 active:scale-95"
              >
                Explore services
              </a>
            </motion.div>
          </div>

          <motion.div
            className="lg:col-span-5 relative hidden lg:block"
            initial={{ opacity: 0, x: 60 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1.1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="relative border border-white/10 overflow-hidden [clip-path:polygon(0_4%,100%_0,100%_96%,0_100%)]">
              <motion.img
                src={HERO_IMG}
                alt="ColdFast refrigerated trucks staged at night"
                data-testid="hero-fleet-image"
                style={{ y: yImg }}
                className="w-full h-[520px] object-cover scale-110"
              />
              <div className="absolute inset-0 bg-abyss/30" aria-hidden="true" />
            </div>
          </motion.div>
        </div>

        <motion.div
          data-testid="hero-stats"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="mt-20 grid grid-cols-2 md:grid-cols-4 border-t border-white/10"
        >
          {STATS.map((stat, i) => (
            <div
              key={stat.label}
              className={`py-6 pr-6 ${i > 0 ? "md:border-l md:border-white/10 md:pl-6" : ""}`}
              data-testid={`hero-stat-${i}`}
            >
              <div className="font-display text-3xl lg:text-4xl font-medium text-snow">{stat.value}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist mt-2 flex items-center gap-2">
                <MapPin className="w-3 h-3 text-frost" strokeWidth={1.5} />
                {stat.label}
              </div>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
